import Phaser from 'phaser';

/**
 * AnimatedDoor — Sprite de porte animée LimeZu.
 *
 * Deux types de spritesheets :
 *   - Open-only (≤ 6 frames) : toutes les frames = ouverture, fermeture = reverse
 *   - Full-cycle (> 6 frames) : première moitié = ouverture, fermeture = reverse de l'ouverture
 *
 * La fermeture est TOUJOURS le reverse de l'ouverture (jamais la seconde moitié du sheet).
 *
 * Origin (0, 1) = bas-gauche, aligné avec le rectangle Tiled.
 */
export class AnimatedDoor extends Phaser.GameObjects.Sprite {
  readonly doorId: string;
  private isOpen = false;
  private readonly openEnd: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    doorId: string,
    frameCount: number,
  ) {
    super(scene, x, y, doorId);
    this.doorId = doorId;

    // Portes ≤ 6 frames : open-only (toutes les frames = ouverture)
    // Portes > 6 frames : full-cycle (première moitié = ouverture)
    this.openEnd = frameCount > 6
      ? Math.floor(frameCount / 2)
      : frameCount - 1;

    this.setFrame(0);
    this.setDepth(5);
    this.setOrigin(0, 1);

    // Ouverture : frames 0 → openEnd
    if (!scene.anims.exists(`${doorId}_open`)) {
      scene.anims.create({
        key: `${doorId}_open`,
        frames: scene.anims.generateFrameNumbers(doorId, {
          start: 0,
          end: this.openEnd,
        }),
        frameRate: 4,
        repeat: 0,
      });
    }

    // Fermeture : reverse de l'ouverture (openEnd → 0)
    if (!scene.anims.exists(`${doorId}_close`)) {
      scene.anims.create({
        key: `${doorId}_close`,
        frames: scene.anims.generateFrameNumbers(doorId, {
          start: this.openEnd,
          end: 0,
        }),
        frameRate: 4,
        repeat: 0,
      });
    }
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.play(`${this.doorId}_open`);
    this.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.emit('door:opened');
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.play(`${this.doorId}_close`);
    this.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.setFrame(0);
      this.emit('door:closed');
    });
  }

  playLocked(): void {
    this.scene.tweens.add({
      targets: this,
      x: this.x + 3,
      duration: 50,
      yoyo: true,
      repeat: 3,
    });
  }
}
